// 何もない空白部を右クリックしたときに開くコンテキストメニュー。
// 項目は「貼り付け」（クリップボードに貼り付け可能な内容が無ければグレーアウト）と
// 「図形を配置」（前回置いた種別の図形を新しく置く。前回が無くてもテキスト図形を置けるため常に活性）の 2 つ。
//
// 表示位置のクランプ・外側クリック / Escape / スクロール / リサイズでの閉じる副作用は
// object-context-menu.tsx と同じ作法に揃える。貼り付け先・配置先は「開いた瞬間の右クリック位置」の
// ステージ座標（emptyContextMenu.stageX/Y）を使い、メニュー項目へマウスを動かしても位置がずれない。

import { AddBoxIconData, ContentPasteIconData } from "@meiden-connect/material-symbols";
import { Icon } from "@meiden-connect/ui";
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useCanvasStore, type EmptyContextMenuState } from "../store/canvas-store";
import { probeClipboardPasteable } from "../input/use-paste-handler";

const MENU_WIDTH_PX = 180;
const MENU_MARGIN_PX = 8;
// offsetHeight を取れないときの高さの見積り。項目 1 つぶん（上下 8px の内余白 + 1 行）が約 40px、
// それに ul の上下パディング（4px × 2）を足す。項目を増減したらこの数式の項目数も合わせる。
const MENU_FALLBACK_ITEM_HEIGHT_PX = 40;
const MENU_FALLBACK_ITEM_COUNT = 2;
const MENU_VERTICAL_PADDING_PX = 8;
const MENU_FALLBACK_HEIGHT_PX =
  MENU_FALLBACK_ITEM_HEIGHT_PX * MENU_FALLBACK_ITEM_COUNT + MENU_VERTICAL_PADDING_PX;

interface Props {
  /** 「貼り付け」実行。貼り付け先のステージ座標を受け取る。 */
  readonly onPaste: (position: { x: number; y: number }) => void;
  /** 「図形を配置」実行。配置先のステージ座標を受け取る。 */
  readonly onPlaceLastObject: (position: { x: number; y: number }) => void;
}

/** 項目のボタン見た目。非活性のときだけカーソルと不透明度を変える。 */
function menuItemStyle(enabled: boolean): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "8px 12px",
    background: "transparent",
    border: "none",
    borderRadius: "var(--radius-1)",
    textAlign: "left",
    cursor: enabled ? "pointer" : "not-allowed",
    opacity: enabled ? 1 : 0.5,
    color: "inherit",
    font: "inherit",
  };
}

export function EmptyAreaContextMenu({ onPaste, onPlaceLastObject }: Props): JSX.Element | null {
  const emptyContextMenu = useCanvasStore((s) => s.emptyContextMenu);
  const closeEmptyContextMenu = useCanvasStore((s) => s.closeEmptyContextMenu);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  // 貼り付け可否（クリップボード内容の有無）。開くたびに best-effort で判定する。
  // 楽観的に活性を初期値にし、判定が "empty" のときだけ非活性へ落とす（"unknown" では活性のまま）。
  const [pasteEnabled, setPasteEnabled] = useState(true);
  // ボタンが離されたことを確認できたメニュー。開くたびに `emptyContextMenu` は新しい値になるため、
  // これを「今開いているメニューか」で比べれば、開き直しのたびに状態を戻す処理が要らない。
  const [releasedMenu, setReleasedMenu] = useState<EmptyContextMenuState | null>(null);

  // ボタンを押したまま開いたメニューは、離されるまでポインタ操作を素通しする（pointer-events: none）。
  // Linux の Chromium のように contextmenu を pointerdown の時点で発火するブラウザでは、
  // 右ボタンドラッグの範囲指定（use-marquee-selection）の途中でメニューがポインタの真下へ現れる。
  // 素通しにしないと、以降の pointermove / pointerup をメニューが受け取ってしまい、
  // メニューの大きさぶんの範囲を右ドラッグで選択できなくなる。
  // Windows では contextmenu が pointerup の後に来る（pointerHeld === false）ため、常に受け付ける。
  const pointerInteractive =
    emptyContextMenu?.pointerHeld !== true || releasedMenu === emptyContextMenu;

  // 解放の監視は useLayoutEffect で登録する。contextmenu は離散イベントなので、その中の状態更新は
  // イベントの処理を抜ける前に同期で描画・commit される。useEffect（描画後に非同期）だと、
  // 素早い右クリックでは監視の登録前に pointerup が通り過ぎ、メニューが素通しのまま残り得る。
  useLayoutEffect(() => {
    if (!emptyContextMenu?.pointerHeld) return undefined;
    // 解放を取りこぼしてメニューが押せないまま残らないよう、ウィンドウが前面から外れたときも受け付ける。
    const release = (): void => setReleasedMenu(emptyContextMenu);
    // 押されているボタンが 1 つも残らなくなったときだけ戻す（メニューを開いたのとは別のボタンを
    // 離しただけでは、進行中のドラッグが続いているため戻さない）。
    const releaseWhenNoButtons = (event: MouseEvent): void => {
      if (event.buttons === 0) release();
    };
    window.addEventListener("pointerup", releaseWhenNoButtons, { capture: true });
    // pointerup を取りこぼした場合の受け皿。ボタンを押していない移動が届いた時点で操作は終わっている。
    window.addEventListener("pointermove", releaseWhenNoButtons, { capture: true });
    window.addEventListener("pointercancel", release, { capture: true });
    window.addEventListener("blur", release);
    return () => {
      window.removeEventListener("pointerup", releaseWhenNoButtons, { capture: true });
      window.removeEventListener("pointermove", releaseWhenNoButtons, { capture: true });
      window.removeEventListener("pointercancel", release, { capture: true });
      window.removeEventListener("blur", release);
    };
  }, [emptyContextMenu]);

  // 開くたびに貼り付け可否を判定する。
  useEffect(() => {
    if (!emptyContextMenu) return;
    let cancelled = false;
    setPasteEnabled(true);
    void probeClipboardPasteable().then((result) => {
      if (!cancelled) setPasteEnabled(result !== "empty");
    });
    return () => {
      cancelled = true;
    };
  }, [emptyContextMenu]);

  // メニュー外クリック / Escape / スクロールで閉じる（object-context-menu と同じ）。
  useEffect(() => {
    if (!emptyContextMenu) return undefined;
    const onDown = (event: MouseEvent | PointerEvent): void => {
      if (menuRef.current && menuRef.current.contains(event.target as Node)) return;
      closeEmptyContextMenu();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") closeEmptyContextMenu();
    };
    const onScroll = (): void => closeEmptyContextMenu();
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("wheel", onScroll, { passive: true });
    window.addEventListener("resize", closeEmptyContextMenu);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("wheel", onScroll);
      window.removeEventListener("resize", closeEmptyContextMenu);
    };
  }, [emptyContextMenu, closeEmptyContextMenu]);

  // 画面端で右や下へはみ出す場合は左 / 上へ寄せる。
  useLayoutEffect(() => {
    if (!emptyContextMenu) {
      setPosition(null);
      return;
    }
    const menuEl = menuRef.current;
    const menuWidth = menuEl?.offsetWidth ?? MENU_WIDTH_PX;
    const menuHeight = menuEl?.offsetHeight ?? MENU_FALLBACK_HEIGHT_PX;
    const maxLeft = window.innerWidth - menuWidth - MENU_MARGIN_PX;
    const maxTop = window.innerHeight - menuHeight - MENU_MARGIN_PX;
    setPosition({
      left: Math.max(MENU_MARGIN_PX, Math.min(emptyContextMenu.clientX, maxLeft)),
      top: Math.max(MENU_MARGIN_PX, Math.min(emptyContextMenu.clientY, maxTop)),
    });
  }, [emptyContextMenu]);

  if (!emptyContextMenu) return null;

  const handlePaste = (): void => {
    if (!pasteEnabled) return;
    onPaste({ x: emptyContextMenu.stageX, y: emptyContextMenu.stageY });
    closeEmptyContextMenu();
  };

  // 「貼り付け」とは逆に、先に閉じてから配置する。付箋・テキストは配置直後に編集モードへ入って
  // 本文へフォーカスするため、後からメニューを閉じるとその片付けでフォーカスを奪い返してしまう。
  const handlePlaceLastObject = (): void => {
    closeEmptyContextMenu();
    onPlaceLastObject({ x: emptyContextMenu.stageX, y: emptyContextMenu.stageY });
  };

  return (
    <ul
      ref={menuRef}
      role="menu"
      aria-label="キャンバスのコンテキストメニュー"
      data-testid="empty-area-context-menu"
      style={{
        position: "fixed",
        left: position?.left ?? emptyContextMenu.clientX,
        top: position?.top ?? emptyContextMenu.clientY,
        margin: 0,
        padding: "4px",
        listStyle: "none",
        minWidth: `${MENU_WIDTH_PX}px`,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-2)",
        boxShadow: "var(--shadow-2)",
        zIndex: 1000,
        fontSize: "13px",
        color: "var(--color-text)",
        pointerEvents: pointerInteractive ? "auto" : "none",
      }}
    >
      <li role="none" style={{ margin: 0 }}>
        <button
          role="menuitem"
          type="button"
          data-testid="empty-area-context-menu-paste"
          disabled={!pasteEnabled}
          onClick={handlePaste}
          style={menuItemStyle(pasteEnabled)}
          onMouseEnter={(event) => {
            if (pasteEnabled) event.currentTarget.style.background = "var(--color-surface-muted)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = "transparent";
          }}
        >
          <Icon d={ContentPasteIconData} />
          <span>貼り付け</span>
        </button>
      </li>
      <li role="none" style={{ margin: 0 }}>
        <button
          role="menuitem"
          type="button"
          data-testid="empty-area-context-menu-place-last-object"
          onClick={handlePlaceLastObject}
          style={menuItemStyle(true)}
          onMouseEnter={(event) => {
            event.currentTarget.style.background = "var(--color-surface-muted)";
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.background = "transparent";
          }}
        >
          <Icon d={AddBoxIconData} />
          <span>図形を配置</span>
        </button>
      </li>
    </ul>
  );
}
